import React, { useMemo, useState } from 'react';
import { useQuery } from 'react-query';

import { actionsColumn } from '../../components/BaseTable/BaseTable';
import MeatballDropdown from '../../components/MeatballDropdown/MeatballDropdown';
import Page from '../../components/Page/Page';
import Title from '../../components/Title/Title';
import FilterableTable from '../../components/FilterableTable/FilterableTable';
import { permConst } from '../../constants/permConst';
import { useUserProfile} from '../../hooks/reduxHooks';
import { displayString } from '../../utils/stringUtils';
import { isAuthorized } from '../../utils/userUtils';
import { getPortsOfEntry, PortOfEntry } from '../../services/portsOfEntryService';
import { Button } from 'react-bootstrap';
import FAIcon from '../../components/FAIcon/FAIcon';
import SavePortOfEntryModal from './modals/SavePortOfEntryModal';
import StatusText from '../../components/StatusText/StatusText';

interface OpenModal {
    type?: 'SAVE_MODAL';
    portOfEntry?: PortOfEntry;
}

const PortsOfEntry = () => {
	const [openModal, setOpenModal] = useState<OpenModal>();
	const userProfile = useUserProfile();
	const canEditPortsOfEntry = isAuthorized(userProfile.permissions, [permConst.PORT_OF_ENTRY.EDIT.ALL]);

	const portsOfEntryQuery = useQuery('getPortsOfEntry', () => getPortsOfEntry());
	const isLoading = portsOfEntryQuery.isLoading;
	const fault = portsOfEntryQuery.isError;

	const ActionCell = ({ original }: { original: Record<string, any> }) => {
		return (
			canEditPortsOfEntry && (
                <MeatballDropdown id="port-of-entry-meatball" toggleSize={'lg'}>
                    <MeatballDropdown.Item
                        key={`edit-${original.id}`}
                        eventKey={original.id}
                        onClick={() =>
                            setOpenModal({
                                type: 'SAVE_MODAL',
                                portOfEntry: {
                                    port_of_entry_code: original.port_of_entry_code,
                                    port_of_entry_name: original.port_of_entry_name,
                                    status: original.status,
                                },
                            })
                        }
                    >
                        Edit Port of Entry
                    </MeatballDropdown.Item>
                </MeatballDropdown>
			)
		);
	};

	const dataFormat = [
		{ Header: 'Code', accessor: 'port_of_entry_code'},
		{ Header: 'Name', accessor: 'port_of_entry_name' },
		{ Header: 'Status', accessor: 'status',
			Cell: ({ value }: { value: string }) => <StatusText>{value}</StatusText>},
        
		{
			...actionsColumn,
			Cell: ActionCell,
		},
	];

	const filterProperties = useMemo(() => new Map(
		Object.entries({
			'Status' : {
				filter: 'status',
				selectableFilters: new Set([
					displayString('ACTIVE'),
					displayString('INACTIVE'),
				])
			},
		})
	), []);

	const onClose = () => {
		setOpenModal({});
	};

	return (
        <Page fault={fault} isLoading={isLoading}>
            <Title title="Manage Ports of Entry"
                    button = {<> {canEditPortsOfEntry && ( 
                        <Button
                            variant="primary"
                            id="addPortOfEntry"
                            onClick={(e) =>
                                setOpenModal({
                                    type: 'SAVE_MODAL',
                                    portOfEntry: undefined,
                                })
                            }
                        >
                            <FAIcon className="mr-1" name="plus" />
                            Add Port of Entry
                        </Button>
                 )} </> }
            />
            <FilterableTable
				data={portsOfEntryQuery.data?.data}
				dataFormat={dataFormat}
				filterProperties={filterProperties}
				searchableFields={['port_of_entry_name']}
				searchableFieldPlaceHolder="Search name..."
				defaultSorted={[
					{
						id: 'status',
						desc: false
					},
					{
						id: 'port_of_entry_name',
						desc: false
					}
				]}
			/>

            <SavePortOfEntryModal
                show={!!openModal && openModal?.type === 'SAVE_MODAL'}
                onCompletion={() => portsOfEntryQuery.refetch()}
                onClose={onClose}
                editPermission={canEditPortsOfEntry}
                portOfEntry={openModal?.portOfEntry}
            />
        </Page>
	);
};

export default PortsOfEntry;
